import { MessageSquareMore, Heart } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useDeletePost } from 'src/features/feeds/api/feeds/use-delete-post'
import { useToggleLike } from 'src/features/feeds/api/likes/use-toggle-like'
import { logger } from 'src/lib/logger'
import { Button } from 'src/shared/components/ui/buttons/button'
import { ImageGrid } from 'src/shared/components/ui/media/image-grid'
import { TextWithLinks } from 'src/shared/components/ui/text-with-links'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useToast } from 'src/shared/hooks/use-toast'
import type { StreamPost } from 'src/shared/schemas/stream/feed'
import { cn } from 'src/shared/utils/cn'

import PostEditor from './post-editor'
import { PostHeader } from './post-header'

interface PostProps {
  post: StreamPost
  showFullContent?: boolean
  isPublic?: boolean
  variant?: 'card' | 'list'
}

const Post = ({ post, showFullContent = false, variant = 'card', isPublic = false }: PostProps) => {
  const author = post.actor
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const toggleLike = useToggleLike()
  const [isEditing, setIsEditing] = useState(false)
  const deletePost = useDeletePost()
  const { toast } = useToast()

  const hasLiked = post.ownReactions?.like && post.ownReactions.like.length > 0
  const likeReactionId = hasLiked ? post.ownReactions?.like?.[0]?.id : undefined

  const handleLikeClick = async (e: React.MouseEvent) => {
    e.stopPropagation()

    try {
      const variables = {
        activityId: post.id,
        parentType: 'post' as const,
        reactionId: hasLiked ? likeReactionId : undefined,
        isLiked: Boolean(hasLiked),
      }
      await toggleLike.mutateAsync(variables)
    } catch (error) {
      logger.error(`Error updating like status for post ID: ${post.id} | ${error}`)
    }
  }

  const handlePostClick = () => {
    navigate(paths.feed.post.getHref(post.id), {
      state: {
        post,
        scrollPosition: window.scrollY,
      },
    })
  }

  const handleDelete = async () => {
    try {
      await deletePost.mutateAsync(post.id)
      toast({
        title: 'Success',
        description: 'Post deleted successfully',
        variant: 'default',
      })
      navigate(paths.feed.path)
    } catch (error) {
      logger.error('Post - Error deleting post:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete post. Please try again.',
        variant: 'destructive',
      })
    }
  }

  if (!author) {
    logger.error('Post - No author data available for post:', post.id)
    return null
  }

  if (!author.profession) {
    logger.warn('Post - No profession data for author:', author.id)
  }

  return (
    <>
      <section
        className={cn(
          'flex flex-col gap-3 bg-background px-4 pb-1 pt-4',
          variant === 'list' ? 'rounded-none' : 'rounded-lg border border-border/50',
        )}
      >
        <PostHeader
          userId={author.id}
          fullName={author.fullName}
          username={author.username}
          profession={author.profession as Profession}
          shortAddress={author.shortAddress}
          timestamp={post.time}
          clickable={!isEditing && !isPublic}
          post={post}
          onEdit={() => setIsEditing(true)}
          onDelete={handleDelete}
        />

        {isEditing ? (
          <PostEditor post={post} onCancel={() => setIsEditing(false)} onSuccess={() => setIsEditing(false)} />
        ) : (
          <>
            <TextWithLinks
              text={post.text || ''}
              className={cn(
                'max-w-full whitespace-pre-wrap break-words text-foreground/90 [word-break:break-word]',
                !showFullContent && !isPublic && 'line-clamp-6 cursor-pointer',
                isMobile && 'text-sm',
              )}
              onClick={isPublic ? undefined : handlePostClick}
            />

            {post.images && post.images.length > 0 && (
              <div onClick={(e) => e.stopPropagation()} className="mt-4">
                <ImageGrid images={post.images} maxImages={5} />
              </div>
            )}
          </>
        )}

        <div className="flex items-center gap-4 text-muted">
          {isPublic ? (
            <>
              <div className="flex items-center gap-1 py-3">
                <Heart
                  className={cn('h-5 w-5', {
                    'fill-current text-secondary': hasLiked,
                  })}
                />
                <span>{post.reactionCounts?.like || 0}</span>
              </div>
              <div className="flex items-center">
                <MessageSquareMore className="mr-1 size-5" />
                <span>{post.reactionCounts?.comment || 0}</span>
              </div>
            </>
          ) : (
            <>
              <Button
                variant="like"
                className={cn(
                  'flex items-center gap-1 p-0 hover:bg-none hover:text-muted',
                  hasLiked && 'text-secondary',
                )}
                onClick={handleLikeClick}
              >
                <Heart
                  className={cn('h-5 w-5', {
                    'fill-current text-secondary hover:text-muted': hasLiked,
                  })}
                />
                <span>{post.reactionCounts?.like || 0}</span>
              </Button>
              <div className="flex cursor-pointer items-center" onClick={handlePostClick}>
                <MessageSquareMore className="mr-1 size-5" />
                <span>{post.reactionCounts?.comment || 0}</span>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  )
}

export default React.memo(Post, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.post.id === nextProps.post.id &&
    prevProps.post.text === nextProps.post.text &&
    prevProps.post.images?.length === nextProps.post.images?.length &&
    prevProps.post.reactionCounts?.like === nextProps.post.reactionCounts?.like &&
    prevProps.post.reactionCounts?.comment === nextProps.post.reactionCounts?.comment &&
    prevProps.post.ownReactions?.like?.length === nextProps.post.ownReactions?.like?.length &&
    prevProps.showFullContent === nextProps.showFullContent &&
    prevProps.variant === nextProps.variant &&
    prevProps.isPublic === nextProps.isPublic
  )
})

export type { PostProps }
